import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { hexToHsv, Hsv, hsvToHex } from './color-math';

/** Chromium's EyeDropper API (not in the TS DOM types yet). */
declare const EyeDropper: { new (): { open(): Promise<{ sRGBHex: string }> } } | undefined;

/**
 * Colour panel after Miro's: an area for saturation (across) and brightness (down), a hue bar,
 * the hex code to type in and, where the browser has one, an eyedropper.
 * `colorChange` while dragging (to preview), `colorCommit` once a colour is settled (pointer up,
 * Enter, eyedropper) - the caller keeps it then, e.g. as an own colour.
 */
@Component({
  selector: 'app-color-panel',
  imports: [CommonModule],
  templateUrl: './color-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./color-panel.component.css'],
})
export class ColorPanelComponent implements OnChanges {
  @Input() value = '#0984c2';
  @Output() colorChange = new EventEmitter<string>();
  @Output() colorCommit = new EventEmitter<string>();

  @ViewChild('area') area?: ElementRef<HTMLElement>;
  @ViewChild('hueBar') hueBar?: ElementRef<HTMLElement>;

  readonly hintHex = $localize`:@@orgChartHexCode:Hex code`;
  readonly hintEyedropper = $localize`:@@colorPanelEyedropper:Pick a colour from the screen`;
  readonly hintArea = $localize`:@@colorPanelArea:Saturation and brightness`;
  readonly hintHue = $localize`:@@colorPanelHue:Hue`;
  readonly canPickFromScreen = typeof EyeDropper !== 'undefined';

  hsv: Hsv = hexToHsv('#0984c2');
  hex = '#0984c2';
  hexInvalid = false;
  private dragging: 'area' | 'hue' | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Not while dragging: the preview comes back in as the new value.
    if (changes['value'] && !this.dragging && /^#[0-9a-f]{6}$/i.test(this.value ?? '')) {
      this.hsv = hexToHsv(this.value.toLowerCase(), this.hsv.h);
      this.hex = this.value.toLowerCase();
    }
  }

  /** Pure hue, the area's background. */
  get hueColor(): string {
    return hsvToHex({ h: this.hsv.h, s: 1, v: 1 });
  }

  onPointerDown(target: 'area' | 'hue', e: PointerEvent): void {
    if (e.button !== 0) return;
    e.preventDefault();
    this.dragging = target;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.track(e);
  }

  onPointerMove(e: PointerEvent): void {
    if (this.dragging) this.track(e);
  }

  onPointerUp(): void {
    if (!this.dragging) return;
    this.dragging = null;
    this.colorCommit.emit(this.hex);
  }

  private track(e: PointerEvent): void {
    const el = (this.dragging === 'area' ? this.area : this.hueBar)?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    this.hsv = this.dragging === 'area' ? { ...this.hsv, s: x, v: 1 - y } : { ...this.hsv, h: x * 360 };
    this.hex = hsvToHex(this.hsv);
    this.hexInvalid = false;
    this.colorChange.emit(this.hex);
  }

  /** Arrow keys move the focused thumb a little, so it works without a mouse too. */
  onKey(target: 'area' | 'hue', e: KeyboardEvent): void {
    const step = e.shiftKey ? 0.1 : 0.02;
    const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
    const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0;
    if (!dx && !dy) return;
    e.preventDefault();
    const clamp = (n: number) => Math.min(1, Math.max(0, n));
    this.hsv =
      target === 'area'
        ? { ...this.hsv, s: clamp(this.hsv.s + dx), v: clamp(this.hsv.v - dy) }
        : { ...this.hsv, h: clamp(this.hsv.h / 360 + dx) * 360 };
    this.hex = hsvToHex(this.hsv);
    this.colorChange.emit(this.hex);
    this.colorCommit.emit(this.hex);
  }

  /** Typed hex: six or three digits, with or without "#"; Enter or leaving the field applies it. */
  applyHex(text: string): void {
    const raw = text.trim().replace(/^#/, '').toLowerCase();
    const full = /^[0-9a-f]{3}$/.test(raw) ? raw.split('').map(c => c + c).join('') : raw;
    if (!/^[0-9a-f]{6}$/.test(full)) {
      this.hexInvalid = text.trim() !== '';
      return;
    }
    if (`#${full}` === this.hex && !this.hexInvalid) return;
    this.hex = `#${full}`;
    this.hsv = hexToHsv(this.hex, this.hsv.h);
    this.hexInvalid = false;
    this.colorChange.emit(this.hex);
    this.colorCommit.emit(this.hex);
  }

  async pickFromScreen(): Promise<void> {
    if (typeof EyeDropper === 'undefined') return;
    try {
      const { sRGBHex } = await new EyeDropper().open();
      this.applyHex(sRGBHex);
    } catch {
      // Cancelled with Esc: nothing to do.
    }
  }
}
